import {
  Body2,
  Box,
  Container,
  DatePicker,
  Display2,
  Flex,
  Grid,
  GridItem,
  Label1,
  Link,
  Tab,
  Tabs,
} from '@sainsburys-tech/fable'
import { CalendarBlank } from '@sainsburys-tech/icons'
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { checkStore, getUserStore } from '../../../utils/localStore'
import { getCounterAvailability } from '../../api/productionApi'
import {
  getCounterTemperatures,
  getTemperatureChecks,
} from '../../api/reportApi'
import { IBatchTemperaturesResponse } from '../../types/IBatchTemperaturesResponse'
import { ICounterAvailability } from '../../types/ICounterAvailability'
import { ICounterTemperaturesResponse } from '../../types/ICounterTemperaturesResponse'
import CounterView from './CounterView'
import TemperatureCheckView from './TemperatureCheckView'

moment.locale('en-GB')

const ReportView = () => {
  const { dispatch } = useContext(Context)
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [activeTab, setActiveTab] = React.useState('temperature-check')
  const [batchDate, setBatchDate] = useState<string>(
    moment(new Date()).format('DD/MM/YYYY'),
  )
  const [batchTemperatures, setBatchTemperatures] =
    useState<IBatchTemperaturesResponse | null>(null)
  const [counterAvailability, setCounterAvailability] = useState<
    ICounterAvailability | undefined
  >(undefined)
  const [counterTemperatures, setCounterTemperatures] =
    useState<ICounterTemperaturesResponse | null>(null)
  const [showDatePicker, setShowDatePicker] = useState(false)

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.HotFood,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    const loadReportData = async () => {
      if (!batchDate || !moment(batchDate, 'DD/MM/YYYY', true).isValid()) {
        return
      }
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [
          batchTempChecksData,
          counterAvailabilityData,
          counterTempData,
          isStoreZeroErrorsEnabled,
        ] = await Promise.all([
          getTemperatureChecks(
            apiClient,
            store.storeId,
            moment(batchDate, 'DD/MM/YYYY').toDate(),
          ),
          getCounterAvailability(
            apiClient,
            store.storeId,
            moment(batchDate, 'DD/MM/YYYY').toDate(),
          ),
          getCounterTemperatures(
            apiClient,
            store.storeId,
            moment(batchDate, 'DD/MM/YYYY').toDate(),
          ),
          isFeatureEnabled(apiClient, Feature.StoreZeroErrors, 0),
        ])

        if (isStoreZeroErrorsEnabled) {
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }

        setBatchTemperatures(batchTempChecksData)
        setCounterAvailability(counterAvailabilityData)
        setCounterTemperatures(counterTempData)
      } catch (e) {
        handleError(ErrorMessage.ReportView.FailedToLoad, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadReportData()
  }, [apiClient, store, batchDate, handleError, resetError])

  const setDateFromPicker = (date: string) => {
    setBatchDate(date)

    setShowDatePicker(false)
  }

  const dateLastMonth = () => {
    const dt = new Date(new Date().toDateString())
    dt.setDate(dt.getDate() - 28)
    return dt
  }

  const hasSingleCabinetType = () => {
    if (
      counterTemperatures === null ||
      counterTemperatures.counterTemperatures?.length === 0
    ) {
      return undefined
    }

    return true
  }

  const renderPage = () => (
    <Box backgroundColor='primary' padding='none'>
      <div className='c-common-hide-in-print'>
        <Header
          title={headerPlannerTitles.hotfood}
          navItems={headerNavigationItems}
          activeMenuType={Menu.Report}
        />
      </div>
      <Container
        style={{
          backgroundColor:
            'var(--fable-color-background-neutral-subtlest-default)',
          padding: '32px 0',
          maxWidth: 1400,
          margin: '0 auto',
        }}
      >
        {errorMessage && <ErrorRefresh message={errorMessage} />}

        {!errorMessage && (
          <Grid gap='lg'>
            <GridItem colSpan={12} className='c-common-hide-in-print'>
              <Display2 style={{ paddingBottom: '16px', marginLeft: '16px' }}>
                Temperature check reports
              </Display2>
              <Body2 style={{ marginLeft: '16px' }}>
                View checks from the past 4 weeks
              </Body2>
            </GridItem>
            <GridItem colSpan={12}>
              <Tabs
                activeTab={activeTab}
                onChange={(event: React.FormEvent<HTMLDivElement>) => {
                  const value = (event.target as HTMLDivElement).getAttribute(
                    'data-value',
                  )
                  if (value) setActiveTab(value)
                }}
              >
                <Tab
                  label='Temp check reports'
                  value='temperature-check'
                  testid='temperature-check-report-tab'
                >
                  <Grid gap='md' cols={12}>
                    <GridItem colSpan={12}>
                      <Flex
                        justifyContent='left'
                        alignItems='center'
                        gap='md'
                        style={{
                          padding: '16px 0',
                        }}
                      >
                        <span>
                          <Label1>
                            {moment(batchDate, 'DD/MM/YYYY', true).isValid()
                              ? `Summary from the ${batchDate}`
                              : 'Please select a date'}
                          </Label1>
                        </span>
                        <Flex alignItems='center' gap='sm'>
                          <CalendarBlank
                            width={20}
                            height={20}
                            style={{
                              color: 'var(--fable-color-icon-brand-1-default)',
                            }}
                          />
                          <Link
                            className='c-common-hover-over-cursor'
                            onClick={() => {
                              setShowDatePicker(true)
                            }}
                            style={{
                              color:
                                'var(--fable-color-icon-brand-1-default) !important',
                            }}
                            testid='change-date-link'
                          >
                            <Body2>Change date</Body2>
                          </Link>
                        </Flex>
                      </Flex>
                      {showDatePicker && (
                        <Box style={{ padding: '0 0 16px 0' }}>
                          <DatePicker
                            label='Date'
                            value={batchDate || ''}
                            onChange={setDateFromPicker}
                            isStandalone
                            testid='hotfood-report-datepicker'
                            shouldCloseOnSelect={true}
                            placeholder={batchDate || 'Date'}
                            range={{
                              from: moment(dateLastMonth()).format(
                                'DD/MM/YYYY',
                              ),
                              to: moment(new Date()).format('DD/MM/YYYY'),
                              state: 'enabled',
                            }}
                          />
                        </Box>
                      )}
                      {!isLoading &&
                        batchDate &&
                        moment(batchDate, 'DD/MM/YYYY', true).isValid() &&
                        batchTemperatures && (
                          <TemperatureCheckView
                            batchDate={batchDate}
                            batchTemperatures={batchTemperatures!}
                            counterAvailability={counterAvailability}
                            hasSingleCabinetType={hasSingleCabinetType()}
                          />
                        )}
                    </GridItem>
                  </Grid>
                </Tab>
                <Tab
                  label='Counter reports'
                  value='counter'
                  testid='counter-report-tab'
                >
                  <Grid gap='md' cols={12}>
                    <GridItem colSpan={12}>
                      <Flex
                        justifyContent='left'
                        alignItems='center'
                        gap='md'
                        style={{
                          padding: '16px 0',
                        }}
                      >
                        <span>
                          <Label1>
                            {moment(batchDate, 'DD/MM/YYYY', true).isValid()
                              ? `Summary from the ${batchDate}`
                              : 'Please select a date'}
                          </Label1>
                        </span>
                        <Flex alignItems='center' gap='sm'>
                          <CalendarBlank
                            width={20}
                            height={20}
                            style={{
                              color: 'var(--fable-color-icon-brand-1-default)',
                            }}
                          />
                          <Link
                            className='c-common-hover-over-cursor'
                            onClick={() => {
                              setShowDatePicker(true)
                            }}
                            style={{
                              color:
                                'var(--fable-color-icon-brand-1-default) !important',
                            }}
                            testid='change-date-link'
                          >
                            <Body2>Change date</Body2>
                          </Link>
                        </Flex>
                      </Flex>
                      {showDatePicker && (
                        <Box style={{ padding: '0 0 16px 0' }}>
                          <DatePicker
                            label='Date'
                            value={batchDate || ''}
                            onChange={setDateFromPicker}
                            isStandalone
                            testid='hotfood-report-datepicker'
                            shouldCloseOnSelect={true}
                            placeholder={batchDate || 'Date'}
                            range={{
                              from: moment(dateLastMonth()).format(
                                'DD/MM/YYYY',
                              ),
                              to: moment(new Date()).format('DD/MM/YYYY'),
                              state: 'enabled',
                            }}
                          />
                        </Box>
                      )}
                    </GridItem>
                    {!isLoading &&
                      batchDate &&
                      moment(batchDate, 'DD/MM/YYYY', true).isValid() &&
                      batchTemperatures && (
                        <GridItem colSpan={12}>
                          <CounterView
                            batchDate={batchDate}
                            counterTemperatureResponse={counterTemperatures!}
                          />
                        </GridItem>
                      )}
                  </Grid>
                </Tab>
              </Tabs>
            </GridItem>
          </Grid>
        )}
      </Container>
    </Box>
  )

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      {!isLoading && renderPage()}
    </>
  )
}

export default ReportView
